/**
 * CRYELA PRODUCT PAGE — assets/cryela-product.js
 *
 * Gallery: all product images live in a flex image-track.
 *   Desktop  → overflow:hidden, JS sets track.scrollLeft to jump between frames
 *   Mobile   → overflow-x:auto + scroll-snap, user swipes freely;
 *              IntersectionObserver syncs thumbnails / color dots / price / CTA
 *
 * No external libraries. Safe when product has no variants / images / colors.
 */

(function () {
  'use strict';

  /* ----------------------------------------------------------------
     Initialise every .cryela-product section on the page
  ---------------------------------------------------------------- */
  function initSection(section) {
    var sectionId = section.dataset.sectionId;
    if (!sectionId) return;

    /* --- Element references --- */
    var track      = section.querySelector('[data-cryela-track]');
    var frames     = Array.prototype.slice.call(section.querySelectorAll('[data-cryela-frame]'));
    var thumbs     = Array.prototype.slice.call(section.querySelectorAll('[data-cryela-thumb]'));
    var colorDots  = Array.prototype.slice.call(section.querySelectorAll('[data-cryela-color-dot]'));
    var variantSel = section.querySelector('[data-cryela-variant-select]');
    var priceMain  = section.querySelector('[data-cryela-price-main]');
    var priceCmp   = section.querySelector('[data-cryela-price-compare]');
    var ctaDesktop = section.querySelector('[data-cryela-cta]');
    var ctaMobile  = document.querySelector('[data-cryela-mobile-cta="' + sectionId + '"]');
    var ctaNav     = section.classList.contains('cryela-product--personalizer')
      ? null
      : document.querySelector('[data-cryela-nav-cta]');

    var configuratorBase = (ctaDesktop && ctaDesktop.dataset.configuratorBase) || '/pages/personalizza';
    var productHandle    = (ctaDesktop && ctaDesktop.dataset.productHandle)    || '';

    /* Sync nav CTA with the initial URL already rendered by the mobile sticky */
    if (ctaNav && ctaMobile) ctaNav.href = ctaMobile.href;

    /* ----------------------------------------------------------------
       HELPERS
    ---------------------------------------------------------------- */

    /** Update all CTA links href (desktop, mobile sticky, bottom-nav) */
    function updateCTAUrl(variantId) {
      if (!variantId) return;
      var url = configuratorBase + '?product=' + productHandle + '&variant=' + variantId;
      if (ctaDesktop) ctaDesktop.href = url;
      if (ctaMobile)  ctaMobile.href  = url;
      if (ctaNav)     ctaNav.href     = url;
    }

    /** Mark the thumbnail that matches imageId as active */
    function activateThumbByImageId(imageId) {
      thumbs.forEach(function (t) {
        t.classList.toggle('is-active', String(t.dataset.cryelaImageId) === String(imageId));
      });
    }

    /** Mark the color dot that matches variantId as active */
    function activateDotByVariantId(variantId) {
      if (!variantId) return;
      colorDots.forEach(function (d) {
        d.classList.toggle('is-active', String(d.dataset.cryelaVariantId) === String(variantId));
      });
    }

    /** Update price display using data stored on the hidden <option> */
    function updatePriceFromVariantId(variantId) {
      if (!variantId || !variantSel) return;
      var opt = variantSel.querySelector('option[value="' + variantId + '"]');
      if (!opt) return;
      if (priceMain) priceMain.textContent = opt.dataset.price || '';
      if (priceCmp) {
        var cr = parseInt(opt.dataset.compareRaw || '0', 10);
        var pr = parseInt(opt.dataset.priceRaw   || '0', 10);
        if (cr > pr && opt.dataset.comparePrice) {
          priceCmp.innerHTML     = '<s>' + opt.dataset.comparePrice + '</s>';
          priceCmp.style.display = '';
        } else {
          priceCmp.style.display = 'none';
        }
      }
      if (variantSel) variantSel.value = variantId;

      var isAvail = opt.dataset.available === 'true';
      document.querySelectorAll('[data-cryela-atc]').forEach(function(b) {
        b.dataset.cryelaVariant = variantId;
        b.disabled = !isAvail;
        b.textContent = isAvail ? 'Aggiungi al carrello' : 'Esaurito';
      });
    }

    /**
     * Scroll the track to show the frame with the given imageId.
     * Desktop: instant (overflow:hidden, JS sets scrollLeft directly).
     * Mobile:  smooth scrollIntoView on the swipeable track.
     */
    function scrollToFrame(imageId) {
      if (!track || !imageId) return;

      var targetFrame = null;
      frames.forEach(function (f) {
        if (String(f.dataset.cryelaFrame) === String(imageId)) targetFrame = f;
      });
      if (!targetFrame) return;

      var isMobile = window.innerWidth <= 850;
      if (isMobile) {
        /* Let the browser smoothly scroll the overflowing track */
        targetFrame.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      } else {
        /* Programmatic jump — works even though track is overflow:hidden */
        track.scrollLeft = targetFrame.offsetLeft;
      }
    }

    /* ----------------------------------------------------------------
       INTERSECTION OBSERVER — mobile swipe → sync UI
       Fires whenever a frame becomes >= 50% visible inside the track
    ---------------------------------------------------------------- */
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;

            var frame     = entry.target;
            var imageId   = frame.dataset.cryelaFrame;
            var variantId = frame.dataset.frameVariantId;

            /* Always sync the thumbnail */
            activateThumbByImageId(imageId);

            /* Only update variant-dependent UI if this frame belongs to a variant */
            if (variantId && variantId !== '' && variantId !== 'undefined') {
              activateDotByVariantId(variantId);
              updatePriceFromVariantId(variantId);
              updateCTAUrl(variantId);
            }
          });
        },
        { threshold: 0.5, root: track }
      );

      frames.forEach(function (f) { observer.observe(f); });
    }

    /* ----------------------------------------------------------------
       THUMBNAIL CLICK → jump to matching frame
    ---------------------------------------------------------------- */
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var imageId = thumb.dataset.cryelaImageId;
        scrollToFrame(imageId);
        /* Optimistically mark active — observer will confirm on mobile */
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });

    /* ----------------------------------------------------------------
       COLOR DOT CLICK → jump to variant image + update price / CTA
    ---------------------------------------------------------------- */
    colorDots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var variantId = dot.dataset.cryelaVariantId;
        var imageId   = dot.dataset.cryelaImageId;
        if (!variantId) return;

        /* Activate this dot */
        colorDots.forEach(function (d) { d.classList.remove('is-active'); });
        dot.classList.add('is-active');

        /* Jump to image */
        scrollToFrame(imageId);
        activateThumbByImageId(imageId);

        /* Update price, variant select, CTA */
        updatePriceFromVariantId(variantId);
        updateCTAUrl(variantId);
      });
    });

    /* ----------------------------------------------------------------
       KEYBOARD SUPPORT — arrow keys on color dots
    ---------------------------------------------------------------- */
    colorDots.forEach(function (dot, i) {
      dot.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          next = colorDots[(i + 1) % colorDots.length];
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          next = colorDots[(i - 1 + colorDots.length) % colorDots.length];
        }
        if (next) {
          e.preventDefault();
          next.focus();
          next.click();
        }
      });
    });

  } /* end initSection */

  /* ----------------------------------------------------------------
     Color-only add-to-cart — AJAX handler (standalone button, no form)
  ---------------------------------------------------------------- */
  function initColorOnlyForms() {
    document.querySelectorAll('[data-cryela-atc]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variantId = btn.dataset.cryelaVariant;
        if (!variantId) return;

        var allBtns = document.querySelectorAll('[data-cryela-atc]');
        allBtns.forEach(function (b) { b.disabled = true; });

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then(function (r) { return r.json(); })
          .then(function () {
            return fetch('/cart.js', { headers: { Accept: 'application/json' } });
          })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: 'cryela-color-only',
                data: {
                  source: 'cryela-color-only',
                  itemCount: cart.item_count,
                  displayItemCount: cart.item_count
                }
              }
            }));
            var drawer = document.querySelector('cart-drawer-component');
            if (drawer) {
              if (typeof drawer.open === 'function') drawer.open();
              else if (typeof drawer.showDialog === 'function') drawer.showDialog();
            } else {
              window.location.href = '/cart';
            }
          })
          .catch(function () {
            window.location.href = '/cart/add?id=' + variantId;
          })
          .finally(function () {
            allBtns.forEach(function (b) { b.disabled = false; });
          });
      });
    });
  }

  /* ----------------------------------------------------------------
     DOM ready — init all sections
  ---------------------------------------------------------------- */
  function init() {
    document.querySelectorAll('.cryela-product').forEach(initSection);
    initColorOnlyForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
